import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import { Button } from '../../ui/button';
import CommentItem from '../../comments/CommentItem';
import MentionTextarea from '../../comments/MentionTextarea';
import { commentsService } from '../../../services/comments';
import { useAuth } from '../../../hooks/useAuth';
import type { Comment } from '../../../types/comment';
import type { ClimbCommentSection } from '../../../types/comments-grouped';

interface ClimbCommentsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  climbSlugOrId: string;
  onCountChange?: (delta: number) => void;
}

export function ClimbCommentsDrawer({
  open,
  onOpenChange,
  climbSlugOrId,
  onCountChange,
}: ClimbCommentsDrawerProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [sections, setSections] = useState<ClimbCommentSection[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState<Record<string, boolean>>({});
  const textareaRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const loadData = async () => {
      setLoading(true);
      try {
        const commentsRes = await commentsService.getCommentsForClimb(climbSlugOrId);
        if (cancelled) return;
        setSections(commentsRes.data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadData();
    return () => {
      cancelled = true;
    };
  }, [open, climbSlugOrId]);

  const setDraft = (ascentLogId: string, value: string) =>
    setDrafts(prev => ({ ...prev, [ascentLogId]: value }));

  const handleReply = useCallback(
    (ascentLogId: string, author: Comment['user']) => {
      const mention = `@[${author.username}](${author.slug}) `;
      setDrafts(prev => {
        const current = prev[ascentLogId] ?? '';
        if (current.includes(mention)) return prev;
        const sep = current && !current.endsWith(' ') ? ' ' : '';
        return { ...prev, [ascentLogId]: current + sep + mention };
      });
      const el = textareaRefs.current[ascentLogId];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const textarea = el.querySelector('textarea');
        textarea?.focus();
      }
    },
    [],
  );

  const handlePost = async (ascentLogId: string) => {
    const body = (drafts[ascentLogId] ?? '').trim();
    if (!body) return;
    setSubmitting(prev => ({ ...prev, [ascentLogId]: true }));
    try {
      const res = await commentsService.createComment(ascentLogId, body);
      setSections(prev =>
        prev.map(section =>
          section.ascent_log.id === ascentLogId
            ? { ...section, comments: [ ...section.comments, res.data ] }
            : section,
        ),
      );
      setDraft(ascentLogId, '');
      onCountChange?.(1);
    } finally {
      setSubmitting(prev => ({ ...prev, [ascentLogId]: false }));
    }
  };

  const handleCommentUpdated = (ascentLogId: string, updated: Comment) => {
    setSections(prev =>
      prev.map(section =>
        section.ascent_log.id === ascentLogId
          ? {
              ...section,
              comments: section.comments.map(c => (c.id === updated.id ? updated : c)),
            }
          : section,
      ),
    );
  };

  const handleCommentDeleted = (ascentLogId: string, commentId: string) => {
    setSections(prev =>
      prev.map(section =>
        section.ascent_log.id === ascentLogId
          ? { ...section, comments: section.comments.filter(c => c.id !== commentId) }
          : section,
      ),
    );
    onCountChange?.(-1);
  };

  const hasAnyComments = sections.some(s => s.comments.length > 0);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{t('home.instagram.commentsTitle')}</SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-6">
          {loading ? (
            <div className="text-sm text-muted-foreground py-6 text-center">…</div>
          ) : !hasAnyComments && sections.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">
              {t('home.instagram.noComments')} {t('home.instagram.beTheFirst')}
            </div>
          ) : (
            sections.map(section => {
              const ascentLog = section.ascent_log;
              const ascentLogId = ascentLog.id;
              const isOwnAscentLog = user && ascentLog.user.id === String(user.id);
              return (
                <div key={ascentLogId} className="space-y-3 border-b pb-4 last:border-b-0">
                  <div className="flex items-center gap-2">
                    <Avatar className="h-7 w-7">
                      {ascentLog.user.avatar_small_url ? (
                        <AvatarImage src={ascentLog.user.avatar_small_url} alt={ascentLog.user.username} />
                      ) : null}
                      <AvatarFallback className="text-[10px]">
                        {ascentLog.user.username.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium">{ascentLog.user.username}</span>
                    {ascentLog.send_type ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-foreground/80 uppercase tracking-wide">
                        {t(`home.instagram.sendType.${ascentLog.send_type}`, ascentLog.send_type)}
                      </span>
                    ) : null}
                  </div>

                  {section.comments.length === 0 ? (
                    <p className="text-xs text-muted-foreground pl-9">
                      {isOwnAscentLog
                        ? `${t('home.instagram.noComments')} ${t('home.instagram.beTheFirst')}`
                        : t('home.instagram.noComments')}
                    </p>
                  ) : (
                    <div className="space-y-3 pl-2">
                      {section.comments.map(comment => (
                        <CommentItem
                          key={comment.id}
                          comment={comment}
                          currentUserId={user?.id?.toString()}
                          ascentLogId={ascentLogId}
                          onUpdated={(updated) => handleCommentUpdated(ascentLogId, updated)}
                          onDeleted={(id) => handleCommentDeleted(ascentLogId, id)}
                          onReply={(author) => handleReply(ascentLogId, author)}
                        />
                      ))}
                    </div>
                  )}

                  {user && (
                    <div ref={el => { textareaRefs.current[ascentLogId] = el; }} className="space-y-2 pl-2">
                      <MentionTextarea
                        value={drafts[ascentLogId] ?? ''}
                        onChange={(v) => setDraft(ascentLogId, v)}
                        disabled={submitting[ascentLogId]}
                        minRows={2}
                      />
                      <div className="flex justify-end">
                        <Button
                          size="sm"
                          onClick={() => handlePost(ascentLogId)}
                          disabled={submitting[ascentLogId] || !(drafts[ascentLogId] ?? '').trim()}
                        >
                          {t('home.instagram.postComment')}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
